import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { requestTyped } from '@xdocs/ws-protocol';
import type { AdminUserDetail, ErrorAlert } from '@xdocs/schemas';
import { useWs } from '../../ws/WsProvider.js';

export interface UseAdminUserDetailResult {
  detail: AdminUserDetail | null;
  isLoading: boolean;
  isError: boolean;
  error: ErrorAlert | null;
  refetch: () => void;
  invalidate: () => void;
}

export function adminUserDetailQueryKey(userId: string): readonly unknown[] {
  return ['admin', 'users', 'detail', userId];
}

/** `admin.users.get` (F54 detail-screen header). */
export function useAdminUserDetail(userId: string): UseAdminUserDetailResult {
  const { wsClient, connectionState } = useWs();
  const queryClient = useQueryClient();
  const enabled = connectionState === 'connected' && userId.length > 0;

  const result = useQuery<AdminUserDetail, ErrorAlert>({
    queryKey: adminUserDetailQueryKey(userId),
    queryFn: () => requestTyped(wsClient, 'admin.users.get', { userId }),
    enabled,
  });

  const invalidate = useCallback(
    () => void queryClient.invalidateQueries({ queryKey: adminUserDetailQueryKey(userId) }),
    [queryClient, userId],
  );

  return {
    detail: result.data ?? null,
    isLoading: result.isLoading,
    isError: result.isError,
    error: result.error ?? null,
    refetch: () => void result.refetch(),
    invalidate,
  };
}
